import { create } from 'zustand';
import { fetchResourcePermissions } from '../../api/resourcePermissions';
import {
  buildResourceIndex,
  can as canPermission,
  canAnyAction,
  mergeResourcePermissionRows,
} from '../../permissions/permissionLogic';
import {
  canSchedulerResource,
  canShowSchedulerNav,
  hasAnySchedulerViewAccess,
} from '../../permissions/scheduler/navAccess';
import {
  canAccessSchedulerPath as canAccessSchedulerPathFn,
  getDefaultSchedulerLandingPath,
} from '../../permissions/scheduler/routeAccess';
import type { SchedulerNavPermission } from '../../permissions/scheduler/resources';
import { SCHEDULER_RESOURCES } from '../../permissions/scheduler/resources';
import type {
  NormalizedResourcePermission,
  PermissionAction,
  PermissionContext,
  ResourcePermissionsStatus,
} from '../../permissions/types';

interface ResourcePermissionsState {
  status: ResourcePermissionsStatus;
  customerId: string | null;
  error: string | null;
  resources: NormalizedResourcePermission[];
  loadedAt: number | null;
  /** Lookup index rebuilt whenever resources change */
  index: Map<string, NormalizedResourcePermission>;

  fetch: (customerId: string, options?: { force?: boolean }) => Promise<void>;
  clear: () => void;

  can: (
    resourceId: string,
    action: PermissionAction,
    context?: PermissionContext,
    options?: { failClosed?: boolean }
  ) => boolean;
  canAny: (
    resourceId: string,
    context?: PermissionContext,
    options?: { failClosed?: boolean }
  ) => boolean;
  canShowNav: (nav: SchedulerNavPermission, context?: PermissionContext) => boolean;
  hasModuleAccess: (context: PermissionContext) => boolean;
  canAccessPath: (pathname: string, context: PermissionContext) => boolean;
  getDefaultLandingPath: (context: PermissionContext) => string | null;
  isReady: () => boolean;
  isLoading: () => boolean;
}

function rebuildIndex(
  resources: NormalizedResourcePermission[]
): Map<string, NormalizedResourcePermission> {
  return buildResourceIndex(resources);
}

export const useResourcePermissionsStore = create<ResourcePermissionsState>((set, get) => ({
  status: 'idle',
  customerId: null,
  error: null,
  resources: [],
  loadedAt: null,
  index: new Map(),

  clear: () => {
    set({
      status: 'idle',
      customerId: null,
      error: null,
      resources: [],
      loadedAt: null,
      index: new Map(),
    });
  },

  fetch: async (customerId, options) => {
    if (!customerId) {
      get().clear();
      return;
    }

    const state = get();
    if (!options?.force && state.status === 'ready' && state.customerId === customerId) {
      return;
    }

    set({ status: 'loading', error: null, customerId });

    try {
      const data = await fetchResourcePermissions(customerId);
      const merged = mergeResourcePermissionRows(data.resources ?? []);
      set({
        resources: merged,
        index: rebuildIndex(merged),
        loadedAt: Date.now(),
        status: 'ready',
        error: null,
        customerId,
      });
    } catch (err) {
      console.error('Failed to fetch resource permissions:', err);
      set({
        error: err instanceof Error ? err.message : 'Failed to load permissions',
        status: 'error',
        resources: [],
        index: new Map(),
        customerId,
      });
    }
  },

  isReady: () => get().status === 'ready',
  isLoading: () => {
    const status = get().status;
    return status === 'loading' || status === 'idle';
  },

  can: (resourceId, action, context = {}, options) => {
    const { status, index } = get();
    const canOptions = {
      ready: status === 'ready',
      failClosed: options?.failClosed ?? true,
    };
    if (
      resourceId === SCHEDULER_RESOURCES.module ||
      resourceId.startsWith(`${SCHEDULER_RESOURCES.module}.`)
    ) {
      return canSchedulerResource(index, resourceId, action, context, canOptions);
    }
    return canPermission(index, resourceId, action, context, canOptions);
  },

  canAny: (resourceId, context = {}, options) => {
    const { status, index } = get();
    return canAnyAction(index, resourceId, context, {
      ready: status === 'ready',
      failClosed: options?.failClosed ?? true,
    });
  },

  canShowNav: (nav, context = {}) => {
    const { status, index } = get();
    return canShowSchedulerNav(index, nav, context, {
      ready: status === 'ready',
      failClosed: true,
    });
  },

  hasModuleAccess: (context) => {
    const { status, index } = get();
    if (status !== 'ready') return false;
    return hasAnySchedulerViewAccess(index, context, {
      ready: true,
      failClosed: true,
    });
  },

  canAccessPath: (pathname, context) => {
    const { status, index } = get();
    return canAccessSchedulerPathFn(index, pathname, context, {
      ready: status === 'ready',
    });
  },

  getDefaultLandingPath: (context) => {
    const { status, index } = get();
    if (status !== 'ready') return null;
    return getDefaultSchedulerLandingPath(index, context);
  },
}));
